import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { CartItem, LastOrder, CatalogProduct } from '../types/catalog';
import { parseMoney } from '../utils/money';

const STORAGE_KEY = 'pwa_client_cart_v1';

function loadFromStorage(): CartItem[] {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as CartItem[];
    } catch {
        return [];
    }
}

export const useCartStore = defineStore('client-cart', () => {
    const items = ref<CartItem[]>(loadFromStorage());

    const total = computed(() => items.value.reduce((sum, i) => sum + i.price * i.quantity, 0));

    const count = computed(() => items.value.reduce((sum, i) => sum + i.quantity, 0));

    function persist(): void {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items.value));
    }

    function clear(): void {
        items.value = [];
        persist();
    }

    function removeItem(variantId: string | null): void {
        if (!variantId) {
            return;
        }
        items.value = items.value.filter(i => i.variantId !== variantId);
        persist();
    }

    function updateQty(variantId: string | null, qty: number): void {
        if (!variantId) {
            return;
        }
        if (qty <= 0) {
            removeItem(variantId);
            return;
        }
        const it = items.value.find(i => i.variantId === variantId);
        if (it) {
            it.quantity = qty;
            persist();
        }
    }

    function addItem(item: CartItem): void {
        const key = `${item.productId}:${item.variantId ?? ''}`;
        const existing = items.value.find(
            i => `${i.productId}:${i.variantId ?? ''}` === key,
        );
        if (existing) {
            existing.quantity += item.quantity;
        } else {
            items.value.push({ ...item });
        }
        persist();
    }

    /**
     * Adiciona produto simples (sem variantes ou variante única implícita).
     */
    function addProductSimple(product: CatalogProduct, quantity: number): void {
        const variant = product.variants[0];
        if (!variant) {
            return;
        }
        addItem({
            productId: product.id,
            variantId: variant.id,
            name: product.name,
            variantName: variant.name,
            price: parseMoney(variant.price),
            quantity,
            imageUrl: product.photo_url,
        });
    }

    function repeatFromLastOrder(order: LastOrder, catalogProducts: CatalogProduct[]): void {
        clear();
        const byId = new Map(catalogProducts.map(p => [p.id, p]));
        for (const line of order.items) {
            if (!line.product_id) {
                continue;
            }
            const p = byId.get(line.product_id);
            if (!p) {
                continue;
            }
            const variant =
                p.variants.find(v => v.id === line.variant_id) ??
                p.variants.find(v => v.is_available) ??
                p.variants[0];
            if (!variant) {
                continue;
            }
            addItem({
                productId: p.id,
                variantId: variant.id,
                name: p.name,
                variantName: variant.name,
                price: parseMoney(line.unit_price),
                quantity: line.quantity,
                imageUrl: p.photo_url,
            });
        }
    }

    return {
        items,
        total,
        count,
        addItem,
        addProductSimple,
        clear,
        removeItem,
        updateQty,
        repeatFromLastOrder,
        persist,
    };
});
